import React from 'react';
import { motion } from 'motion/react';
import { InfoIcon, MinusIcon, UsersIcon } from '@/components/icons';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';
import type { AddOnResponse, SelectedAddOn, CartItemResponse } from '@/features/cart/types';
import { getAddOnById } from '@/features/cart/utils/cartItemUtils';


interface CartItemEditModeProps {
  cartItem: CartItemResponse;
  editAddOnsPerGuest: SelectedAddOn[][];
  addOns: AddOnResponse[];
  removedGuests: Set<number>;
  removedItems: Set<string>;
  onAddOnClick: (addOnId: string, guests: number[]) => void;
  getEditModeGuestsWithAddOn: (addOnId: string) => number[];
  isItemMarkedForRemoval: (guestIndex: number, addOnId: string) => boolean;
  removeAddOnCompletely: (addOnId: string) => void;
  restoreAddOnCompletely: (addOnId: string) => void;
  isAddOnCompletelyRemoved: (addOnId: string) => boolean;
}

const CartItemEditMode: React.FC<CartItemEditModeProps> = ({
  cartItem,
  editAddOnsPerGuest,
  addOns,
  removedGuests,
  onAddOnClick,
  getEditModeGuestsWithAddOn,
  isItemMarkedForRemoval,
  removeAddOnCompletely,
  restoreAddOnCompletely,
  isAddOnCompletelyRemoved,
}) => {
  const formatPrice = (amount: number) => formatCurrencyForDisplay(amount, cartItem.currency);
  const uniqueAddOnIds = Array.from(new Set(
    editAddOnsPerGuest
      .filter(guestAddOns => guestAddOns != null)
      .flatMap(guestAddOns => 
        guestAddOns.map(ao => ao.add_on_id)
      )
  ));

  if (uniqueAddOnIds.length === 0) {
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-white font-medium text-lg">Edit Add-ons</span>
          <div className="relative group">
            <button
              className="p-1.5 text-gray-400 hover:text-blue-400 rounded-full hover:bg-blue-500/10 transition-colors"
              title="Click on the addon to remove it from guests"
            >
              <InfoIcon className="w-4 h-4" />
            </button>
            <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10 border border-gray-700">
              Click on the addon to remove it from guests
              <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>
        <div className="text-center py-8 text-gray-400">
          <p>No add-ons selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-white font-medium text-lg">Edit Add-ons</span>
        <div className="relative group">
          <button
            className="p-1.5 text-gray-400 hover:text-blue-400 rounded-full hover:bg-blue-500/10 transition-colors"
            title="Click on the addon to remove it from guests"
          >
            <InfoIcon className="w-4 h-4" />
          </button>
          <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10 border border-gray-700">
            Click on the addon to remove it from guests
            <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
        {uniqueAddOnIds.map((addOnId) => {
          const addOnData = getAddOnById(addOns, addOnId);
          if (!addOnData) return null;
          
          const guestsWithAddOn = getEditModeGuestsWithAddOn(addOnId);
          const hasValidImage = addOnData.image_id && addOnData.image_id !== '00000000-0000-0000-0000-000000000000';
          const imageUrl = hasValidImage ? getSignedMediaUrl(addOnData.image_id, 'image').url : null;
          
          // Check if this add-on is completely removed
          const isCompletelyRemoved = isAddOnCompletelyRemoved(addOnId);
          
          // Calculate total price (only for active guests)
          const totalPrice = editAddOnsPerGuest.reduce((sum, guestAddOns, guestIndex) => {
            if (!guestAddOns || isItemMarkedForRemoval(guestIndex, addOnId) || removedGuests.has(guestIndex)) return sum;
            const selectedAddOn = guestAddOns.find(ao => ao.add_on_id === addOnId);
            return sum + (selectedAddOn?.price || 0);
          }, 0);

          const handleCardClick = (e: React.MouseEvent) => {
            // Prevent card click when addon is marked for removal
            if (isCompletelyRemoved) {
              e.stopPropagation();
              return;
            }
            onAddOnClick(addOnId, guestsWithAddOn);
          };

          const handleMinusClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            removeAddOnCompletely(addOnId);
          };

          const handleUndoClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            restoreAddOnCompletely(addOnId);
          };

          return (
            <motion.div
              key={addOnId}
              whileHover={isCompletelyRemoved ? {} : { scale: 1.02 }}
              whileTap={isCompletelyRemoved ? {} : { scale: 0.98 }}
              onClick={handleCardClick}
              className={`relative aspect-[4/3] rounded-lg overflow-hidden transition-all border-2 ${
                isCompletelyRemoved 
                  ? 'opacity-50 cursor-not-allowed border-gray-600' 
                  : 'cursor-pointer border-gray-700 hover:border-blue-500/50'
              }`}
              style={{
                background: imageUrl 
                  ? `url(${imageUrl})`
                  : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
              
              {/* Grey-out overlay when completely removed */}
              {isCompletelyRemoved && (
                <div className="absolute inset-0 bg-gray-900/60 z-30" />
              )}
              
              {/* Guest Count Indicator - Top Right */}
              <div className="absolute top-2 right-10 z-20 flex items-center gap-1 bg-black/60 rounded-full px-1.5 py-0.5">
                <UsersIcon className="w-3 h-3 text-white" />
                <span className="text-white text-xs font-medium">{guestsWithAddOn.length}</span>
              </div>
              
              {/* Minus/Undo Button - Top Right */}
              <div className="absolute top-2 right-2 z-40">
                {isCompletelyRemoved ? (
                  <button
                    onClick={handleUndoClick}
                    className="p-1 bg-blue-500/80 hover:bg-blue-500 rounded-full transition-colors shadow-lg"
                    title="Undo removal"
                  >
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={handleMinusClick}
                    className="p-1 bg-red-500/80 hover:bg-red-500 rounded-full transition-colors shadow-lg"
                    title="Remove addon from all guests"
                  >
                    <MinusIcon className="w-3 h-3 text-white" />
                  </button>
                )}
              </div>
              
              {/* Content */}
              <div className="absolute inset-0 flex flex-col justify-between p-2.5 z-20">
                <div className="flex-1" />
                
                {/* Name and Price */}
                <div>
                  <h5 className="font-semibold text-sm mb-0.5 line-clamp-1 text-white">
                    {addOnData.name}
                  </h5>
                  <p className="font-bold text-sm text-blue-400">
                    {formatPrice(totalPrice)}
                  </p>
                </div>
              </div>
            </motion.div>
          );
        }).filter(Boolean)}
      </div>
    </div>
  );
};

export default CartItemEditMode;

